import {
  Restriction,
  useCreateRestrictionMutation,
  useDeleteRestrictionMutation,
  useUpdateRestrictionMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import { ResultTypeFrom } from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";

export type UseRestrictionMutationProps = {}

export type UseRestrictionMutationResult = {
  createRestriction: (restriction: Partial<Restriction>, onSuccess: (() => void) | undefined) => void
  updateRestriction: (restriction: Partial<Restriction>, onSuccess: (() => void) | undefined) => void
  deleteRestriction: (id: string, onSuccess: (() => void) | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useRestrictionMutation = (props: UseRestrictionMutationProps = {}): UseRestrictionMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createRestriction, {isLoading: isCreating}] = useCreateRestrictionMutation()
  const [_updateRestriction, {isLoading: isUpdating}] = useUpdateRestrictionMutation()
  const [_deleteRestriction, {isLoading: isDeleting}] = useDeleteRestrictionMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useRestrictionUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.restriction", "Restriction"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useRestrictionUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useRestrictionUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.restriction", "Restriction"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useRestrictionUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useRestrictionUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.restriction", "Restriction"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useRestrictionUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.restriction", "Restriction") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.restriction", "Restriction") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.restriction", "Restriction") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteConfirm = useCallback((id: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deleteRestriction({id: id}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deleteRestriction, handleDeleteResult])

  const createRestriction = useCallback((restriction: Partial<Restriction>, onSuccess: (() => void) | undefined) => {
    const promise = _createRestriction(restriction).unwrap()
    handleCreateResult(promise, onSuccess)
  }, [_createRestriction, handleCreateResult])

  const updateRestriction = useCallback((restriction: Partial<Restriction>, onSuccess: (() => void) | undefined) => {
    const promise = _updateRestriction(restriction).unwrap()
    handleUpdateResult(promise, onSuccess)
  }, [_updateRestriction, handleUpdateResult])

  const deleteRestriction = useCallback((id: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.restriction", "Restriction"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.delete.restriction.confirm", "Are you sure you want to delete this restriction?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(id, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createRestriction: createRestriction,
    updateRestriction: updateRestriction,
    deleteRestriction: deleteRestriction,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
